<template>
    <TaxesTable>
        <template #actions>
            <CreateButton
                v-if="userStore.can('create_taxes')"
                :title="$t('create_tax')"
                @click="createTaxModalOpen = true"
            />
        </template>
    </TaxesTable>
    <CreateTaxModal
        v-if="createTaxModalOpen"
        v-model:open="createTaxModalOpen"
        @cancel="closeTaxModal"
        @submit="createTax"
    />
    <EditTaxModal
        v-if="editTaxModalOpen"
        v-model:open="editTaxModalOpen"
        :tax="currentTax"
        @cancel="closeTaxModal"
        @submit="editTax"
    />
    <DeleteItemModal
        id="delete-tax-modal"
        title="delete_tax_question"
        :open="deleteTaxModalOpen"
        :model-value="currentTax"
        id-key="tax_uid"
        @cancel="deleteTaxModalOpen = false"
        @submit="deleteTaxConfirm"
    />
</template>

<script setup lang="ts">
import DeleteItemModal from '@src/components/DeleteItemModal.vue'
import { useTable } from '@src/composables/common'
import CreateTaxModal from '@src/partials/taxes/CreateTaxModal.vue'
import { TableSorting } from '@src/types'
import { TaxData, TaxForm } from '@src/types/Tax'
import { deleteTax, getTaxes, storeTax, updateTax } from '@src/utils/api/taxes'
import { useTaxesStore } from '@src/utils/stores/taxes'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import CreateButton from '../actions/CreateButton.vue'
import EditTaxModal from './EditTaxModal.vue'

const router = useRouter()
const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const taxesStore = useTaxesStore()
const { taxesData } = storeToRefs(taxesStore)

const currentTax = ref<TaxData>({} as TaxData)
const deleteTaxModalOpen = ref(false)
const createTaxModalOpen = ref(false)
const editTaxModalOpen = ref(false)

const { TableWrapper: TaxesTable } = useTable({
    loadData: loadTaxes,
    filters: {
        name: '',
    },
    disableFilters: [],
    tableProps: {
        id: 'taxes-table',
        label: 'taxes',
        help: 'help_taxes_table',
        pagination: true,
        data: taxesData,
        total: taxesData.value.length,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'ID',
                key: 'tax_uid',
                select: true,
            },
            {
                label: 'ID',
                key: 'tax_uid',
            },
            {
                label: 'name',
                key: 'name',
            },
            {
                label: 'target',
                key: 'target',
            },
            {
                label: 'issuer_country',
                key: 'issuer_country',
            },
            {
                label: 'investor_country',
                key: 'investor_country',
            },
            {
                label: 'created_at',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: { rowData: TaxData }) => {
                    return moment(props.rowData.created_at).format('DD.MM.yyyy')
                },
            },
        ],
        actions: [
            {
                action: navigateToShowTax,
                icon: 'fa-eye',
                title: 'view',
                condition: true,
            },
            {
                action: (rowData: TaxData) => {
                    editTaxModalOpen.value = true
                    currentTax.value = rowData
                },
                icon: 'fa-solid fa-pen',
                title: 'edit',
                condition: userStore.can('update_taxes'),
            },
            {
                action: openDeleteTaxModal,
                icon: 'fas fa-trash-alt',
                title: 'delete',
                color: 'text-danger-500',
                condition: userStore.can('delete_taxes'),
            },
        ],
    },
})

async function loadTaxes() {
    const getTaxesRequest = await getTaxes()
    taxesData.value = getTaxesRequest.data.taxes
}

function navigateToShowTax(rowData: TaxData) {
    router.push(`/taxes/${rowData.tax_uid}`)
}

function openDeleteTaxModal(rowData: TaxData) {
    currentTax.value = rowData
    deleteTaxModalOpen.value = true
}

function closeTaxModal() {
    editTaxModalOpen.value = false
    createTaxModalOpen.value = false
    currentTax.value = {} as TaxData
    taxesStore.resetTaxForm()
}

const createTax = async (form: TaxForm) => {
    try {
        const response = await storeTax(form)
        if (response.status === 201) {
            createTaxModalOpen.value = false
            toast.success(i18n.t('toast_success_tax_created'))
            taxesStore.resetTaxForm()
            await loadTaxes()
        }
    } catch (error) {
        console.error(error)
    }
}

const editTax = async (form: TaxForm) => {
    const updateTaxRequest = await updateTax(currentTax.value.tax_uid, form)
    if (updateTaxRequest.status === 200) {
        editTaxModalOpen.value = false
        currentTax.value = {} as TaxData
        taxesStore.resetTaxForm()
        await loadTaxes()
        toast.success(i18n.t('toast_success_tax_updated'))
    } else {
        toast.error(i18n.t('toast_error_tax_updated'))
    }
}

async function deleteTaxConfirm(rowData: TaxData) {
    try {
        const deleteTaxRequest = await deleteTax(rowData.tax_uid)
        if (deleteTaxRequest.status === 204) {
            deleteTaxModalOpen.value = false
            currentTax.value = {} as TaxData
            await loadTaxes()
        }
    } catch (error) {
        console.error(error)
    }
}

onMounted(async () => {
    await loadTaxes()
})
</script>
